import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { setSelectedCategory, fetchMenuDataRequest, MenuItem } from '../redux/slices/menuSlice';
import { addToCart, openCart } from '../redux/slices/cartSlice';
import { fetchComboRequest } from '../redux/slices/comboSlice';
import { createCartItem } from '../utils/cartUtils';
import { shouldShowSpiceLevel } from '../utils/spiceUtils';
import { filterEnabledModifiers, isModifierRequired } from '../utils/modifierUtils';
import { parsePrice } from '../utils/priceUtils';
import { 
  StatusMessage, 
  MenuGrid, 
  CategoryFilter, 
  MenuSearch,
  BackButton
} from '../components';
import { AddToCartModal } from '../components/AddToCartModal';
import DietaryFilters, { DietaryFilter, useDietaryFilterLogic, applyDietaryFilters } from '../components/DietaryFilters';
import MenuToggle from '../components/MenuToggle';
import ComboCard from '../components/ComboCard';

const MenuPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const appNavigate = useAppNavigate();
  const { loading, error, data: menuData, selectedCategory } = useAppSelector(state => state.menu);
  const { data: comboData } = useAppSelector(state => state.combo);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [modalItem, setModalItem] = useState<MenuItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeDietaryFilters, setActiveDietaryFilters] = useState<Set<DietaryFilter>>(new Set());
  const [isComboMode, setIsComboMode] = useState(false);
  const { handleDietaryFilterToggle } = useDietaryFilterLogic();

  // Fetch menu data and combo data on component mount if not already loaded
  useEffect(() => {

      dispatch(fetchMenuDataRequest());
      dispatch(fetchComboRequest());
    
  }, []);

  const handleCategoryChange = useCallback((category: string | null) => {
    dispatch(setSelectedCategory(category));
  }, [dispatch]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleItemClick = useCallback((item: any) => {
    appNavigate(`/menu-item/${item.pk_id}`);
  }, [appNavigate]);

  // Check if item needs customization before adding to cart
  const needsCustomization = useCallback((item: MenuItem): boolean => {
    const hasSpiceLevel = shouldShowSpiceLevel(item);
    const enabledModifiers = filterEnabledModifiers(item.modifiers_list || []);
    const hasRequiredModifiers = enabledModifiers.some(modifier => isModifierRequired(modifier));
    return hasSpiceLevel || hasRequiredModifiers;
  }, []);

  const handleAddToCart = useCallback((item: MenuItem) => {
    if (needsCustomization(item)) {
      // Open modal for customization
      setModalItem(item);
      setIsModalOpen(true);
    } else {
      // Add directly to cart for simple items
      const cartItem = {
        item,
        selected: {
          spice: null,
          modifiers: [],
        },
        totalPrice: parsePrice(item.price),
        cartItemId: `${item.pk_id}-basic-${Date.now()}`,
      };
      console.log('#############cartItem############',cartItem)
      dispatch(addToCart(cartItem));
      // Automatically open cart after adding item
      dispatch(openCart());
    }
  }, [dispatch, needsCustomization]);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setModalItem(null);
  }, []);

  const handleModalAddToCart = useCallback((
    item: MenuItem, 
    quantity: number, 
    spiceLevel: number | null, 
    modifiers: any[]
  ) => {
    const cartItem = createCartItem(item, spiceLevel, modifiers, quantity);
    dispatch(addToCart(cartItem));
    // Close modal and open cart
    setIsModalOpen(false);
    setModalItem(null);
    dispatch(openCart());
  }, [dispatch]);

  // Handle dietary filter toggle using the logic from DietaryFilters component
  const onDietaryFilterToggle = useCallback((filter: DietaryFilter) => {
    handleDietaryFilterToggle(filter, activeDietaryFilters, setActiveDietaryFilters);
  }, [handleDietaryFilterToggle, activeDietaryFilters]);

  // Check if combo data exists and has enabled combos
  const hasComboData = useMemo(() => {
    console.log('Combo data check:', { comboData, isArray: Array.isArray(comboData), combosLength: comboData?.length });
    return comboData && Array.isArray(comboData) && comboData.length > 0;
  }, [comboData]);

  const enabledCombos = useMemo(() => {
    if (!comboData || !Array.isArray(comboData)) return [];
    return comboData.filter((combo: any) => combo.is_enabled === true);
  }, [comboData]);

  const totalComboCount = comboData?.length || 0;

  // Handle combo add to cart
  const handleComboAddToCart = useCallback((combo: any) => {
    const comboCartItem = {
      item: {
        pk_id: `combo-${combo.combo_id}`,
        name: combo.title,
        description: combo.description,
        price: combo.combo_price.replace('$', ''),
        image: combo.combo_image,
        is_combo: true,
        combo_data: combo,
      },
      selected: {
        spice: null,
        modifiers: [],
      },
      totalPrice: parsePrice(combo.combo_price),
      cartItemId: `combo-${combo.combo_id}-${Date.now()}`,
      quantity: 1,
    };
    console.log()
    dispatch(addToCart(comboCartItem));
    dispatch(openCart());
  }, [dispatch]);

  // Filter combos based on search and category
  const filteredCombos = useMemo(() => {
    let filtered = enabledCombos;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((combo: any) =>
        combo.title.toLowerCase().includes(query) ||
        combo.description.toLowerCase().includes(query)
      );
    }

    // Apply default sorting by title
    return [...filtered].sort((a: any, b: any) => a.title.localeCompare(b.title));
  }, [enabledCombos, searchQuery]);

  // Filter and sort menu items
  const filteredAndSortedMenuItems = useMemo(() => {
    if (!menuData) return [];

    let filtered = menuData;

    // Apply dietary filters first
    if (activeDietaryFilters.size > 0) {
      filtered = applyDietaryFilters(menuData, comboData || [], activeDietaryFilters);
    }

    // Apply category filter
    if (selectedCategory) {
      filtered = filtered.filter(item => 
        item.category === selectedCategory || 
        item.level1_category === selectedCategory ||
        item.level2_category === selectedCategory
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query)
      );
    }

    // Apply default sorting by name
    const sorted = [...filtered].sort((a, b) => a.name.localeCompare(b.name));

    return sorted;
  }, [menuData, selectedCategory, searchQuery, activeDietaryFilters, comboData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow-md p-6">
                  <div className="h-48 bg-gray-300 rounded mb-4"></div>
                  <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <StatusMessage message={`Error Loading Menu: ${error}`} type="error" />;
  }

  if (!menuData || menuData.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🍽️</div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">No Menu Available</h2>
            <p className="text-gray-600">
              Menu items are not available at the moment. Please check back later.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Back Button and Title */}
          <div className="mb-8">
            <BackButton text="Back to Home" onClick={() => appNavigate('/')} />
            <h1 className="text-4xl font-bold">Our Menu</h1>
          </div>

          {/* Search Bar */}
          <div className="mb-8">
            <MenuSearch
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
            />
          </div>

          {/* Category Filter - Only show for individual items */}
          {!isComboMode && (
            <div className="mb-8">
              <CategoryFilter
                menuItems={menuData}
                selectedCategory={selectedCategory}
                onCategoryChange={handleCategoryChange}
              />
            </div>
          )}

          {/* Menu Toggle - Show if combo data exists */}
          {hasComboData && (
            <MenuToggle
              isComboMode={isComboMode}
              onToggle={setIsComboMode}
              comboCount={totalComboCount}
            />
          )}

          {/* Dietary Filters - Only show for individual items */}
          {!isComboMode && (
            <div className="mb-8">
              <DietaryFilters
                activeDietaryFilters={activeDietaryFilters}
                onDietaryFilterToggle={onDietaryFilterToggle}
                hasEnabledCombos={false}
              />
            </div>
          )}

          {/* Results Count */}
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              {isComboMode 
                ? `Showing ${filteredCombos.length} of ${enabledCombos.length} combos`
                : `Showing ${filteredAndSortedMenuItems.length} of ${menuData.length} items`
              }
            </p>
          </div>

          {/* Content Display */}
          <div>
            {isComboMode ? (
              // Combo Grid
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCombos.length > 0 ? (
                  filteredCombos.map((combo: any) => (
                    <ComboCard
                      key={combo.combo_id}
                      combo={combo}
                      variant="basic"
                      onAddToCart={handleComboAddToCart}
                    />
                  ))
                ) : (
                  <div className="col-span-full text-center py-12">
                    <div className="text-6xl mb-4">🍽️</div>
                    <h2 className="text-2xl font-semibold text-gray-900 mb-2">No Combos Available</h2>
                    <p className="text-gray-600">
                      {searchQuery ? 'No combos match your search.' : 'No combo offers are currently available.'}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              // Individual Items Grid
              <MenuGrid
                menuItems={filteredAndSortedMenuItems}
                selectedCategory={null}
                searchQuery=""
                onItemClick={handleItemClick}
                onAddToCart={handleAddToCart}
              />
            )}
          </div>
        </div>
      </div>

      {/* Add to Cart Modal */}
      <AddToCartModal
        isOpen={isModalOpen}
        item={modalItem}
        onClose={handleModalClose}
        onAddToCart={handleModalAddToCart}
      />
    </>
  );
};

export default MenuPage;
